import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { portal } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [portalKey, setPortalKey] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  // The sign-in dialog lives here rather than in a page, because the things
  // that need it — a locked listing, the navbar — are all over the app, and
  // navigating somebody to /dashboard to sign in loses whatever they were
  // looking at. `after` is what to run once they are in.
  const [authPrompt, setAuthPrompt] = useState(null);
  const afterAuth = useRef(null);

  // Hydrate from localStorage on mount, then validate against /portal/me.
  useEffect(() => {
    const key = localStorage.getItem('loreto_key');
    const storedUserRaw = localStorage.getItem('loreto_user');
    if (!key) {
      setBootstrapping(false);
      return;
    }
    setPortalKey(key);
    if (storedUserRaw) {
      try { setUser(JSON.parse(storedUserRaw)); } catch { /* ignore */ }
    }
    portal
      .me()
      .then((r) => {
        setUser(r.user);
        localStorage.setItem('loreto_user', JSON.stringify(r.user));
        localStorage.setItem('loreto_email', r.user.email);
      })
      .catch(() => {
        localStorage.removeItem('loreto_key');
        localStorage.removeItem('loreto_user');
        localStorage.removeItem('loreto_email');
        setUser(null);
        setPortalKey(null);
      })
      .finally(() => setBootstrapping(false));
  }, []);

  /**
   * Ask the visitor to sign in, and carry on afterwards.
   *
   * Returns true if they were ALREADY signed in, so a caller can just do the
   * thing instead of waiting: `if (requireAuth(...)) doIt()`.
   */
  const requireAuth = useCallback((reason, after) => {
    if (user && portalKey) return true;
    afterAuth.current = after || null;
    setAuthPrompt({ reason: reason || '' });
    return false;
  }, [user, portalKey]);

  const closeAuthPrompt = useCallback((succeeded) => {
    setAuthPrompt(null);
    const next = afterAuth.current;
    afterAuth.current = null;
    if (succeeded && typeof next === 'function') next();
  }, []);

  const setSession = useCallback((nextUser, nextKey) => {
    setUser(nextUser);
    setPortalKey(nextKey);
    if (nextUser) localStorage.setItem('loreto_user', JSON.stringify(nextUser));
    if (nextUser?.email) localStorage.setItem('loreto_email', nextUser.email);
    if (nextKey) localStorage.setItem('loreto_key', nextKey);
  }, []);

  // Merge fresh fields into the session user (e.g. after a profile/avatar update)
  // and persist, without touching the portal key.
  const updateUser = useCallback((nextUser) => {
    if (!nextUser) return;
    setUser(nextUser);
    localStorage.setItem('loreto_user', JSON.stringify(nextUser));
    if (nextUser.email) localStorage.setItem('loreto_email', nextUser.email);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('loreto_key');
    localStorage.removeItem('loreto_user');
    localStorage.removeItem('loreto_email');
    setUser(null);
    setPortalKey(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        portalKey,
        isAuthed: Boolean(user && portalKey),
        bootstrapping,
        setSession,
        updateUser,
        logout,
        requireAuth,
        authPrompt,
        closeAuthPrompt,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
